const REFERRAL_DRAFT_KEY = "defuse_ref_code"
const WELCOME_PENDING_KEY = "defuse_ref_welcome_pending"
const WELCOME_SEEN_KEY = "defuse_ref_welcome_shown"
const ATTRIBUTION_KEY_PREFIX = "defuse_ref_attributed"

const REFERRAL_STATE_CHANGED_EVENT = "referral:state-changed"

// Storage access throws under iOS Safari private mode — swallow.
function safeGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function safeSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {}
}

function safeRemove(storage: Storage, key: string): void {
  try {
    storage.removeItem(key)
  } catch {}
}

export function getReferralDraft(): string | null {
  return safeGet(localStorage, REFERRAL_DRAFT_KEY)
}

// Last-link-wins: clears SEEN so the new outcome can re-open the dialog.
export function setReferralDraft(code: string): void {
  if (safeGet(localStorage, REFERRAL_DRAFT_KEY) === code) return

  safeSet(localStorage, REFERRAL_DRAFT_KEY, code)
  safeRemove(localStorage, WELCOME_SEEN_KEY)
  notifyReferralStateChanged()
}

// `expectedCode` guards the tab-2 race: don't erase a fresh draft mid-flight.
export function clearReferralDraft(expectedCode?: string): void {
  if (
    expectedCode != null &&
    safeGet(localStorage, REFERRAL_DRAFT_KEY) !== expectedCode
  ) {
    return
  }
  safeRemove(localStorage, REFERRAL_DRAFT_KEY)
}

// "not_claimable" was removed as an outcome; stale values persisted by older
// clients fail this validation and resolve to null, so they never render.
const WELCOME_OUTCOMES = ["success"] as const
export type WelcomeOutcome = (typeof WELCOME_OUTCOMES)[number]

function isWelcomeOutcome(v: string): v is WelcomeOutcome {
  return (WELCOME_OUTCOMES as readonly string[]).includes(v)
}

export function getPendingWelcome(): WelcomeOutcome | null {
  if (safeGet(localStorage, WELCOME_SEEN_KEY) != null) return null
  const value = safeGet(localStorage, WELCOME_PENDING_KEY)
  return value && isWelcomeOutcome(value) ? value : null
}

export function markWelcomePending(outcome: WelcomeOutcome): void {
  safeSet(localStorage, WELCOME_PENDING_KEY, outcome)
  safeRemove(localStorage, WELCOME_SEEN_KEY)
  notifyReferralStateChanged()
}

export function hasWelcomeSeen(): boolean {
  return safeGet(localStorage, WELCOME_SEEN_KEY) != null
}

export function markWelcomeSeen(): void {
  safeSet(localStorage, WELCOME_SEEN_KEY, "1")
  safeRemove(localStorage, WELCOME_PENDING_KEY)
}

function attributionKey(address: string, code: string): string {
  return `${ATTRIBUTION_KEY_PREFIX}:${address}:${code}`
}

export function hasAttributionAttempted(
  address: string,
  code: string
): boolean {
  return safeGet(sessionStorage, attributionKey(address, code)) != null
}

export function markAttributionAttempted(address: string, code: string): void {
  safeSet(sessionStorage, attributionKey(address, code), "1")
}

export function clearAttributionAttempt(address: string, code: string): void {
  safeRemove(sessionStorage, attributionKey(address, code))
}

function notifyReferralStateChanged(): void {
  try {
    window.dispatchEvent(new Event(REFERRAL_STATE_CHANGED_EVENT))
  } catch {}
}

export function onReferralStateChanged(handler: () => void): () => void {
  if (typeof window === "undefined") return () => {}
  window.addEventListener(REFERRAL_STATE_CHANGED_EVENT, handler)
  return () => window.removeEventListener(REFERRAL_STATE_CHANGED_EVENT, handler)
}
